// useNearbyPeers — projects mesh.peers into the F10.1 Peer shape.
//
// Combines:
//   - useMesh().peers — presence-derived (handle/avatarSeed/deviceType/publicIp/lastSeen)
//   - useSameLAN(peer.userId) — F10.2 derivation
//   - usePeerSignal(peer.userId) — F10.3 RTT → bars
//
// Output:
//   {
//     peers: Peer[],     // filtered + sorted
//     scanning: boolean, // true while usePublicIp().status === 'pending'
//   }
//
// Filtering: drops anyone whose lastSeen is older than 2 minutes — the
// radar should reflect *currently here* peers, even though presence rows
// linger for 3 minutes (the extra minute keeps the chat presence stack
// stable while a peer steps away).
//
// Sorting:
//   1. signalBars descending (4 > 3 > 2 > 1; null sorts last)
//   2. handle alphabetic ascending (case-insensitive, locale-aware)
//
// Reading every peer's signal via a hook-in-a-loop is illegal in React.
// Instead we compute bars inline from the same primitives — useMesh exposes
// connectionsByPeer so we can call probeRtt outside a per-peer hook. We
// poll a single timer here that walks every PC, keeping the work bounded
// by the mesh cap.

import { useEffect, useMemo, useState } from 'react';
import { useMesh } from './useMesh';
import { usePublicIp } from './usePublicIp';
import { probeRtt, rttToBars } from '../lib/p2p/rttProbe';

const POLL_MS = 5000;
const ACTIVITY_WINDOW_MS = 2 * 60 * 1000; // 2 min — F10.1 spec

const IPV4_RE = /^(\d{1,3}\.){3}\d{1,3}$/;

function sameLanCheck(selfIp, peerIp) {
  if (!selfIp || !peerIp) return false;
  if (!IPV4_RE.test(selfIp) || !IPV4_RE.test(peerIp)) return false;
  return selfIp === peerIp;
}

/**
 * @returns {{
 *   peers: Array<{
 *     userId: string,
 *     handle: string,
 *     avatarSeed: string,
 *     deviceType: 'phone' | 'monitor',
 *     sameLAN: boolean,
 *     signalBars: 1 | 2 | 3 | 4 | null,
 *     lastSeenMs: number,
 *   }>,
 *   scanning: boolean,
 * }}
 */
export function useNearbyPeers() {
  const { peers: meshPeers, connectionsByPeer } = useMesh();
  const { ip: selfIp, status: ipStatus } = usePublicIp();

  // Bars per peer, updated on a single timer that walks every active PC.
  // Index by userId; missing entries default to null.
  const [barsByPeer, setBarsByPeer] = useState({});

  // The set of peer ids we currently have RTCPeerConnection refs for. We
  // re-bind the polling effect whenever this changes so a brand-new peer
  // gets immediate measurement instead of waiting up to POLL_MS.
  const peerIdsKey = useMemo(
    () => Object.keys(connectionsByPeer).sort().join('|'),
    [connectionsByPeer],
  );

  useEffect(() => {
    const ids = Object.keys(connectionsByPeer);
    if (ids.length === 0) {
      setBarsByPeer({});
      return undefined;
    }
    let cancelled = false;

    const tick = async () => {
      const next = {};
      for (const id of ids) {
        const pc = connectionsByPeer[id];
        if (!pc) {
          next[id] = null;
          continue;
        }
        // eslint-disable-next-line no-await-in-loop
        const { rttMs } = await probeRtt(pc);
        next[id] = rttToBars(rttMs);
      }
      if (!cancelled) setBarsByPeer(next);
    };

    tick();
    const interval = setInterval(tick, POLL_MS);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
    // peerIdsKey captures membership; connectionsByPeer captures the PC refs
    // themselves (a peer's PC can swap on reconnect within the same id set).
  }, [peerIdsKey, connectionsByPeer]);

  const peers = useMemo(() => {
    const now = Date.now();
    const selfKnown = !!selfIp && IPV4_RE.test(selfIp);
    const shaped = meshPeers
      .filter((p) => {
        // Aged out of the activity window → gone.
        if (now - (p.lastSeen || 0) > ACTIVITY_WINDOW_MS) return false;
        // Drop peers we can POSITIVELY place on a DIFFERENT network — e.g. a
        // device that dropped Wi-Fi for cellular (it keeps heartbeating, so it
        // never ages out, but its public IP no longer matches ours). The radar
        // is "nearby on your network", so an off-network peer shouldn't show.
        // Tolerant: only exclude when BOTH sides have a known IPv4 that differ;
        // if either IP is unknown (STUN failed / IPv6), keep the peer rather
        // than risk hiding a genuinely-nearby device.
        if (
          selfKnown &&
          p.publicIp &&
          IPV4_RE.test(p.publicIp) &&
          p.publicIp !== selfIp
        ) {
          return false;
        }
        return true;
      })
      .map((p) => ({
        userId: p.userId,
        handle: p.handle,
        avatarSeed: p.avatarSeed,
        deviceType: p.deviceType,
        sameLAN: sameLanCheck(selfIp, p.publicIp),
        signalBars: barsByPeer[p.userId] ?? null,
        lastSeenMs: p.lastSeen,
      }));

    // Sort: bars desc (null last), then handle alpha.
    shaped.sort((a, b) => {
      const ba = a.signalBars ?? -1;
      const bb = b.signalBars ?? -1;
      if (bb !== ba) return bb - ba;
      return String(a.handle || '').localeCompare(String(b.handle || ''));
    });

    return shaped;
  }, [meshPeers, selfIp, barsByPeer]);

  return {
    peers,
    scanning: ipStatus === 'pending',
  };
}

export const __test__ = { POLL_MS, ACTIVITY_WINDOW_MS };

export default useNearbyPeers;
